import actions from '@/store/modules/entity_browser/actions';
import mutations from '@/store/modules/entity_browser/mutations';
import { ascending } from '@/utils/tools';
import { Tree } from '@/utils/tree';
import DefaultColumns, { isHiddenPredicate } from '@/mappings/default_columns';
import {
  merge,
  isEqual,
  uniqBy,
  keys,
  difference,
  pick,
  negate,
  values,
} from 'lodash';
import { groups } from 'd3-array';
import { Status } from '@/utils/useData';
import { buildQuery, joinQuery } from './iql-builder';
import { BrowserMode, TreeMode, FilterMode } from './config';

const store = (mode = BrowserMode.DOC, systemConfig = {}) => {
  const initialState = {

    isFiltersToolbarOpen: false,
    isEntityTableDirty: false,
    filterMode: FilterMode.BASIC,
    filterParams: { },
    savedFilters: [],
    advancedModeFilterQuery: '',

    simpleSearchTerm: '',
    simpleSearchCompactMode: false,

    documentsPerPage: {},
    totalDocumentsCount: 0,
    documentsStatus: Status.IDLE,
    documentsStatusMessage: '',
    selectedDocumentRows: {},
    shiftSelectAnchor: undefined,

    isInsertMode: false,

    treeMode: TreeMode.CATEGORY,
    treeRecursiveMode: false,
    tree: {
      [TreeMode.CATEGORY]: {
        selectedNode: null,
        expandedNodes: [],
        nodes: [],
        status: Status.IDLE,
      },
      [TreeMode.DIRECTORY]: {
        selectedNode: null,
        expandedNodes: [],
        nodes: [],
        status: Status.IDLE,
      },
    },
    treeSortBy: treeSortByKey(mode, systemConfig), // eslint-disable-line
    tablePrimarySort: tableSortByObject(mode), // eslint-disable-line
    tableUserSort: null,
    columns: DefaultColumns[mode],
    inspectedDocument: null,
  };

  // State reducer. reduces state to only those values you want to save.
  // NOTE: the shape of the persisted state should not be changed
  // See: https://github.com/championswimmer/vuex-persist#reducer
  const reducer = (s) => ({
    simpleSearchCompactMode: s.simpleSearchCompactMode,
    filterMode: s.filterMode,
    treeRecursiveMode: s.treeRecursiveMode,
    treeMode: s.treeMode,
    tree: Object.entries(s.tree).reduce((acc, [treeMode, value]) => ({
      ...acc,
      [treeMode]: {
        selectedNode: value.selectedNode,
      },
    }), {}),
    columns: Object.entries(s.columns).reduce((acc, [key, col]) => ({
      ...acc,
      [key]: {
        active: col.active,
        order: col.order,
      },
    }), {}),
  });

  const defaultSettings = reducer(initialState);

  const areSettingsChanged = (state) => {
    const currentSettings = reducer(state);
    return !isEqual(currentSettings, defaultSettings);
  };

  const resetSettings = (state) => {
    merge(state, defaultSettings);
    // temporary fix for filter params:
    state.filterParams = {};
  };

  return {
    namespaced: true,
    state: initialState,
    getters: {
      enabledFilterParams: ({ filterParams }) => Object.values(filterParams).filter((f) => f.active || f.default),
      optionalFilterParams: ({ filterParams }) => Object.values(filterParams).filter((f) => !f.default),
      ftsFilterEnabled: ({ filterParams }) => Boolean(filterParams.fts_filter
        && filterParams.fts_filter.value
        && filterParams.fts_filter.value.some((v) => Boolean(v))),
      uiQuery(state, getters) {
        const { advancedModeFilterQuery } = state;
        return state.filterMode === FilterMode.BASIC ? getters.basicModeFilterQuery : advancedModeFilterQuery;
      },
      basicModeFilterQuery: (_, { enabledFilterParams }) => buildQuery(enabledFilterParams),

      prefilterQueryParam(state, getters, rootState, { route }) {
        return route?.query?.prefilter ?? '';
      },

      query(_, { uiQuery, prefilterQueryParam }) {
        return joinQuery([uiQuery, prefilterQueryParam]);
      },

      /**
       * @returns `true` if there is an active search query - i.e., one or more filter params; an advanced IQL query
       */
      hasQuery(_, { query }) {
        return query !== '';
      },

      /**
       * @returns `true` - when the `All categories/directories' option is selected
       */
      isAllSelected(_, { selectedTreeNode }) {
        return selectedTreeNode === '';
      },

      /**
       * In some use cases, we want to disable the option to select all nodes
       * @returns `true` if disabled
       */
      isAllSelectedDisabled() {
        return false;
      },

      /**
       * @returns `true` - if the user has selected either `All categories` or a specific category/directory.
       */
      isNodeSelected(_, { selectedTreeNode }) {
        return selectedTreeNode !== null;
      },

      isTreeRecursiveModeDisabled(_, { isAllSelected }) {
        return isAllSelected;
      },

      /**
       * Using a getter allows us to override the user-defined `recursive mode` value in some use cases
       * @returns the `tree recursuve mode` state
       */
      treeRecursiveModeGetter({ treeRecursiveMode }, { isAllSelected }) {
        return isAllSelected || treeRecursiveMode;
      },

      tree: (state) => {
        const { tree, treeMode } = state;
        return tree[treeMode];
      },

      selectedTreeNode: (_, { tree }) => tree.selectedNode,
      expandedNodes: (_, { tree }) => tree.expandedNodes,
      treeStatus: (_, { tree }) => tree.status,

      treeNodes: (state, { tree }, rootState) => {
        const { browserMode } = rootState;

        const nodes = tree.nodes || [];
        const nodeFilter = {
          [BrowserMode.MAM]: (n) => n.is_ac_enabled,
          [BrowserMode.DOC]: () => true,
        }[browserMode];

        return Tree.filter(nodes, nodeFilter);
      },

      // treeNodeExists: (_, { tree }) => (nodeId) => tree.nodes.some((node) => node.id === nodeId),

      selectedTreeNodeObject: (_, { tree }) => {
        if (!tree.selectedNode) return null;

        const nodes = tree.nodes || [];
        return Tree.find(nodes, (n) => n.id === tree.selectedNode);
      },

      selectedDocuments: ({ selectedDocumentRows }) => Object.values(selectedDocumentRows),
      selectedDocumentsCount: (_, { selectedDocuments }) => selectedDocuments.length,
      isEveryDocumentSelected: ({ selectedDocumentRows }) => (docs) => docs.every((doc) => Boolean(selectedDocumentRows[`[${doc.nodeId}][${doc.url}]`])),

      /**
       * documents grouped by node id
       * duplicated copy-pages are removed (keeping the first occurrence)
       * the `uri_id` prop is used to compare for uniqueness (copy-pages have unique uri_ids within a single node/document)
       */
      documentsByNode(_, { documents }) {
        return groups(documents, (d) => d.node_id)
          .map(([, docs]) => uniqBy(docs, (d) => d.uri_id));
      },

      /** flat list of documents */
      documents({ documentsPerPage }) {
        return Object.values(documentsPerPage).flat();
      },

      documentsByNodeId: (_, { documents }) => (nodeId) => documents.filter((doc) => doc.node_id === nodeId),

      isMasterDocumentSelected: (_, { documentsByNodeId, isEveryDocumentSelected }) => (nodeId) => isEveryDocumentSelected(documentsByNodeId(nodeId)),

      isHiddenField(state, getters, { user }) {
        if (user?.isSuperuser) {
          return () => false;
        }

        return isHiddenPredicate(systemConfig.MAM_HIDDEN_FIELDS);
      },

      sortedColumns({ columns }, { isHiddenField }) {
        const cols = Object.values(columns);

        return cols
          .filter(({ name }) => !isHiddenField(name))
          .sort((a, b) => ascending(a.order, b.order));
      },
      activeColumns({ isInsertMode }, { sortedColumns }) {
        return sortedColumns
          .filter((c) => !(c.name === 'select' && isInsertMode))
          .filter((c) => c.active);
      },

      /**
       * @returns array of custom columns
       */
      customColumns({ columns }, { isHiddenField }) {
        const customColumnNames = difference(keys(columns), keys(DefaultColumns[mode]));
        const notHidden = negate(isHiddenField);
        return values(pick(columns, customColumnNames.filter(notHidden)));
      },

      areSettingsChanged,

      tableSortSetting({ filterMode, tablePrimarySort, tableUserSort }, { ftsFilterEnabled }) {
        const relevanceSort = { order: 'relevance', desc: 1 };
        if (filterMode !== FilterMode.BASIC) {
          return tablePrimarySort;
        }

        if (ftsFilterEnabled) {
          return tableUserSort === null ? relevanceSort : tableUserSort;
        }

        return tablePrimarySort;
      },
    },
    persistence: {
      reducer,
      filter: (mutation) => mutation.type.startsWith(`${mode}/`),
    },
    actions,
    mutations: {
      ...mutations,
      resetSettings,
    },
  };
};

function tableSortByObject(mode) {
  const columns = DefaultColumns[mode];

  return { order: columns.finished_at.name, desc: 1 };
}

function treeSortByKey(mode, systemConfig = {}) {
  const orderByNameKey = {
    [BrowserMode.DOC]: 'doc_cat_order_by_name',
    [BrowserMode.MAM]: 'mam_cat_order_by_name',
  }[mode];

  return systemConfig[orderByNameKey] ? 'name' : 'orderid';
}

export default store;
